import type { ComponentProps, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';

type BodyProps = Omit<ComponentProps<'div'>, 'children' | 'className'> & {
  className?: string;
};

export interface ChannelSettingsModalProps {
  open: boolean;
  onCancel(): void;
  title: ReactNode;
  header?: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  contentClassName?: string;
  bodyClassName?: string;
  bodyProps?: BodyProps;
  footerClassName?: string;
}

/** Shared shell for the large searchable channel-settings dialogs. */
export function ChannelSettingsModal({
  open,
  onCancel,
  title,
  header,
  children,
  footer,
  contentClassName,
  bodyClassName,
  bodyProps,
  footerClassName,
}: ChannelSettingsModalProps) {
  const { className: bodyPropsClassName, ...restBodyProps } = bodyProps ?? {};

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogContent
        className={cn(
          'flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl',
          contentClassName
        )}
      >
        <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
          <DialogTitle>{title}</DialogTitle>
          {header}
        </DialogHeader>
        <div
          {...restBodyProps}
          className={cn(
            'h-[55vh] min-h-0 overflow-hidden',
            bodyClassName,
            bodyPropsClassName
          )}
        >
          {children}
        </div>
        <DialogFooter
          className={cn('shrink-0 border-t px-6 py-4', footerClassName)}
        >
          {footer}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
